// 🔧 organization_health.js
// ✅ Purpose: Organization Health report page
// ✅ Features:
// - Loads a single Organization
// - Displays Organization summary
// - Displays Health score
// - Displays Diagnostics
// - Displays Statistics
// - Displays Relationship summary
// - Future Repair Actions section
// - Future Audit History section
// - Uses Platform Console layout
// - Read-only MVP
//
// ✅ Notes:
// - Route expected: /admin/organizers/:organizationId/health
// - Opened from the Organizers table Health button
// - Reuses Organizer Console components
// - Firestore reads remain page-local for this MVP

import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";

import {
  Link,
  useParams,
} from "react-router-dom";

import { db } from "../../../firebase";

//import PlatformPageLayout from "../../../components/platform/platform_page_layout";
//import PlatformLoadingState from "../../../components/platform/platform_loading_state";
//import PlatformEmptyState from "../../../components/platform/platform_empty_state";

import OrganizationStatistics from "./components/organization_statistics";
import OrganizationHealthPanel from "./components/organization_health_panel";
import OrganizationDiagnosticsPanel from "./components/organization_diagnostics_panel";
import OrganizationRelationshipSection from "./components/organization_relationship_section";

const OrganizationHealth = () => {
  const { organizationId } =
    useParams();

  const [organization, setOrganization] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [statusMessage, setStatusMessage] =
    useState("");

  const [statusVariant, setStatusVariant] =
    useState("success");

  useEffect(() => {
    
    const loadOrganization =
    async () => {

        setLoading(true);

        try {

        //--------------------------------------------------
        // Call Organization Engine
        //--------------------------------------------------

        const {
            httpsCallable,
        } = await import(
            "firebase/functions"
        );

        const {
            functions,
        } = await import(
            "../../../firebase"
        );

        const organizationHealthReport =
            httpsCallable(
            functions,
            "organizationHealthReport"
            );

        const response =
            await organizationHealthReport({

            organizationId,

            });

        const {

            organization,

            statistics,

            health,

        } = response.data;

        //--------------------------------------------------
        // Merge response
        //--------------------------------------------------

        setOrganization({

            ...organization,

            eventCount:
            statistics.eventCount,

            memberCount:
            statistics.memberCount,

            communityCount:
            statistics.communityCount || 0,

            hostedEvents:
            statistics.hostedEvents || [],

            members:
            statistics.members || [],

            warningCount:
            health.warningCount,

            errorCount:
            health.errorCount,

            healthScore:
            health.healthScore,

            healthStatus:
            health.healthStatus,

            diagnostics:
            health.diagnostics || [],

        });

        } catch (error) {

        console.error(

            "Failed loading organization health:",

            error

        );

        setStatusVariant(
            "danger"
        );

        setStatusMessage(

            "Unable to load organization health."

        );

        } finally {

        setLoading(false);

        }

    };

    if (organizationId) {
      loadOrganization();
    }
  }, [
    organizationId,
  ]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div
          className="spinner-border text-success"
          role="status"
        >
          <span className="visually-hidden">
            Loading...
          </span>
        </div>

        <div className="mt-3">
          Loading Organization Health...
        </div>
      </div>
    );
  }

  if (!organization) {

    return (

      <section
        id="top-content"
        className="container-fluid py-5"
      >

        <div
          className="container"
          style={{ maxWidth: "1200px" }}
        >

          <div className="card shadow-sm">

            <div className="card-body text-center">

              <h2>

                Organization Not Found

              </h2>

              <p className="text-muted">

                This organization could not be found.

              </p>

              <Link
                to="/admin/organizers"
                className="btn btn-secondary"
              >

                Back to Organizers

              </Link>

            </div>

          </div>

        </div>

      </section>

    );

  }

return (

  <section
    id="top-content"
    className="container-fluid py-5"
  >

    <div
      className="container"
      style={{ maxWidth: "1200px" }}
    >

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>

          <h1 className="mb-1">

            🏢 {organization.name}

          </h1>

          <div className="text-muted">

            {organization.organizationSlug}

          </div>

        </div>

        <Link
          to="/admin/organizers"
          className="btn btn-outline-secondary"
        >

          ← Back to Organizers

        </Link>

      </div>

      {statusMessage && (

        <div
          className={`alert alert-${statusVariant}`}
        >

          {statusMessage}

        </div>

      )}
      title={`🏢 ${
        organization.name ||
        "Organization Health"
      }`}
      description={`Health report for ${
        organization.organizationSlug ||
        organization.id
      }`}
      statusMessage={statusMessage}
      statusVariant={statusVariant}
      toolbar={
        <Link
          to="/admin/organizers"
          className="btn btn-outline-secondary"
        >
          ← Back to Organizers
        </Link>
      }
    >
      <OrganizationStatistics
        organization={organization}
      />

      <hr className="my-4" />

      <OrganizationHealthPanel
        organization={organization}
      />

      <hr className="my-4" />

      <OrganizationDiagnosticsPanel
        organization={organization}
      />

      <hr className="my-4" />

      <h5 className="mb-3">
        Relationships
      </h5>

      <OrganizationRelationshipSection
        title="Hosted Events"
        count={organization.eventCount}
      >
        {organization.hostedEvents?.length ? (
          <ul className="list-group">
            {organization.hostedEvents.map(
              (event) => (
                <li
                  key={event.id}
                  className="list-group-item"
                >
                  <div className="fw-semibold">
                    {event.eventName ||
                      event.name ||
                      event.eventSlug ||
                      event.slug ||
                      event.id}
                  </div>

                  <small className="text-muted">
                    {event.relationshipTypes?.join(", ") ||
                      event.relationshipType ||
                      "host"}
                  </small>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="text-muted">
            No hosted events.
          </div>
        )}
      </OrganizationRelationshipSection>

      <OrganizationRelationshipSection
        title="Members"
        count={organization.memberCount}
      >
        {organization.members?.length ? (
          <ul className="list-group">
            {organization.members.map(
              (member) => (
                <li
                  key={member.id}
                  className="list-group-item"
                >
                  <div className="fw-semibold">
                    {member.displayNameSnapshot ||
                      member.displayName ||
                      member.emailSnapshot ||
                      member.email ||
                      member.id}
                  </div>

                  <small className="text-muted">
                    {member.role || "member"}
                  </small>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="text-muted">
            No organization members.
          </div>
        )}
      </OrganizationRelationshipSection>

      <OrganizationRelationshipSection
        title="Community"
        count={organization.communityCount}
      >
        <div className="text-muted">
          Community Engine relationships will appear here.
        </div>
      </OrganizationRelationshipSection>

      <hr className="my-4" />

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <strong>
            Repair Actions
          </strong>
        </div>

        <div className="card-body text-muted">
          Repair actions will be available in a future pass.
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <strong>
            Audit History
          </strong>
        </div>

        <div className="card-body text-muted">
          Organization audit history will appear here in a future pass.
        </div>
      </div>
    </div>

    </section>
    );
};

export default OrganizationHealth;